/**
 * "Your requests" (docs/ux/project-tab-spec.md §1.3): the aggregate state line,
 * the row glyph and the live action tree of a barked item. Never stored: computed on this device
 * from the item's targets joined with task entities and run state, so it cannot go stale.
 */
import type { BarkAction, BarkRecord, BarkRoutingUsed } from '@viko/protocol'
import { routeLabel } from './bark-route.js'

export interface AggTask {
  id: string
  title: string
  status: string
  runId?: string | null
  landedAt?: number | null
  blockedReason?: string | null
}
export interface AggRun {
  runId: string
  status: string
  title?: string | undefined
  activity?: string | undefined
  blockedOn?: string | undefined
  landing?: { state: string; sha?: string | undefined } | undefined
  latestBuild?: { number: number; lane: string; state: string } | undefined
}
/** outcome-polish: a build card and the tasks it includes (follow-through to *in build t3*). */
export interface AggBuild {
  buildId: string
  title: string
  version: string | null
  status: string
  laneId: string
  taskIds: string[]
  createdAt: number | null
}
export interface AggContext {
  tasks: ReadonlyMap<string, AggTask>
  runs: ReadonlyMap<string, AggRun>
  now: number
  /** Build cards of the project (newest first or any order); optional. */
  builds?: readonly AggBuild[]
  /** bark-route: machine id → name, for "Routing used" lines. */
  machines?: ReadonlyMap<string, string>
}

const BUILDING = new Set(['queued', 'building', 'uploading', 'processing'])
/** "0.4.13-t1" when the build has a version, else its title ("Build 3 · test"). */
export function buildLabel(b: Pick<AggBuild, 'version' | 'title'>): string {
  return b.version ?? b.title
}
/** The newest build that includes any of `taskIds`. */
export function buildFor(taskIds: readonly string[], ctx: AggContext): AggBuild | undefined {
  const ids = new Set(taskIds)
  return [...(ctx.builds ?? [])]
    .filter((b) => b.taskIds.some((t) => ids.has(t)))
    .sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0))[0]
}

export type BarkGlyph = 'triaging' | 'active' | 'needs-you' | 'done' | 'failed' | 'undone'
export const BARK_GLYPH_LABEL: Record<BarkGlyph, string> = {
  triaging: 'Sorting it',
  active: 'In progress',
  'needs-you': 'Needs you',
  done: 'Done',
  failed: 'Failed',
  undone: 'Undone',
}

/** Per-target lane state (the subset of laneTaskState the rail needs). */
export type TargetState =
  | 'queued'
  | 'running'
  | 'verifying'
  | 'landed'
  | 'needs-you'
  | 'blocked'
  | 'failed'
  | 'cancelled'
  | 'answered'
  | 'steered'

export function taskState(t: AggTask | undefined, run?: AggRun): TargetState {
  if (!t) return 'queued'
  if (t.status === 'cancelled') return 'cancelled'
  if (t.status === 'done') return 'landed'
  if (run?.status === 'waiting') return 'needs-you'
  if (run?.status === 'error') return 'failed'
  if (t.status === 'blocked') return 'blocked'
  if (t.status === 'review' || t.status === 'landing') return 'verifying'
  if (t.status === 'running') return 'running'
  return 'queued'
}

function runState(r: AggRun | undefined): TargetState {
  if (!r) return 'steered'
  if (r.status === 'waiting') return 'needs-you'
  if (r.status === 'error') return 'failed'
  return 'steered'
}

export interface BarkAggregate {
  glyph: BarkGlyph
  /** `<verb> · <counts>`, needs-you first. */
  line: string
  needsYou: number
  /** Why it bubbled back to the top of Active (first blocker), when it did. */
  reason?: string
  terminal: boolean
  /** When the item reached its terminal state (for ageing out of Active). */
  settledAt?: number
}

interface Counted {
  running: number
  queued: number
  verifying: number
  landed: number
  needs: number
  blocked: number
  answered: number
  steered: number
  tasks: number
  reason?: string
}

function count(actions: BarkAction[], ctx: AggContext): Counted {
  const c: Counted = {
    running: 0,
    queued: 0,
    verifying: 0,
    landed: 0,
    needs: 0,
    blocked: 0,
    answered: 0,
    steered: 0,
    tasks: 0,
  }
  for (const a of actions) {
    if (a.decision === 'answered') c.answered++
    for (const t of a.targets) {
      if (t.type === 'task') {
        const task = ctx.tasks.get(t.id)
        const run = task?.runId ? ctx.runs.get(task.runId) : undefined
        const s = taskState(task, run)
        if (s === 'cancelled') continue
        c.tasks++
        if (s === 'running') c.running++
        else if (s === 'queued') c.queued++
        else if (s === 'verifying') c.verifying++
        else if (s === 'landed') c.landed++
        else if (s === 'needs-you') {
          c.needs++
          c.reason ??= `${task?.title ?? 'A task'} is waiting on you${run?.blockedOn ? `: ${run.blockedOn}` : ''}`
        } else {
          c.blocked++
          c.reason ??= `${task?.title ?? 'A task'} is blocked${task?.blockedReason ? `: ${task.blockedReason}` : ''}`
        }
      } else if (t.type === 'run' && (t.role === 'steered' || t.role === undefined)) {
        const run = ctx.runs.get(t.id)
        const s = runState(run)
        if (s === 'needs-you') {
          c.needs++
          c.reason ??= `${run?.title ?? 'The run'} is waiting on you`
        } else if (s === 'failed') {
          c.blocked++
          c.reason ??= `${run?.title ?? 'The run'} failed`
        } else c.steered++
      } else if (t.type === 'decision') {
        c.needs++
      }
    }
  }
  return c
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`

function verb(actions: BarkAction[], c: Counted): string {
  if (actions.length > 1) return plural(actions.length, 'action')
  const a = actions[0]
  if (!a) return 'Handled'
  switch (a.decision) {
    case 'answered':
      return 'Answered'
    case 'steered':
      return c.steered + c.needs + c.blocked > 1 ? `Steered ${c.steered} runs` : 'Steered the run'
    case 'tasks':
    case 'started':
      return `Became ${plural(c.tasks, 'task')}`
    case 'merged':
      return 'Merged into existing work'
    case 'parked':
      return 'Parked'
    case 'asked':
      return 'Viko asked you'
    case 'noted':
      return 'Noted'
    default:
      return 'Handled'
  }
}

export function barkAggregate(b: BarkRecord, ctx: AggContext): BarkAggregate {
  const history = (k: string) => [...b.history].reverse().find((h) => h.kind === k)
  if (b.status === 'queued')
    return { glyph: 'triaging', line: 'Waiting for triage', needsYou: 0, terminal: false }
  if (b.status === 'triaging')
    return { glyph: 'triaging', line: 'Working out where this goes…', needsYou: 0, terminal: false }
  if (b.status === 'failed') {
    const why = history('failed')?.note
    return {
      glyph: 'failed',
      line: `Failed${why ? `: ${why}` : ''}`,
      needsYou: 0,
      terminal: false,
      ...(why ? { reason: why } : {}),
    }
  }
  if (b.status === 'undone' || b.status === 'cancelled')
    return {
      glyph: 'undone',
      line: b.status === 'undone' ? 'Undone' : 'Cancelled',
      needsYou: 0,
      terminal: true,
      settledAt: b.updatedAt,
    }
  const active = b.actions.filter((a) => a.status === 'active')
  const c = count(active, ctx)
  const openProposals = b.proposals.filter((p) => p.status === 'open').length
  const lastAgent = [...b.thread].reverse().find((m) => m.author !== 'system')
  const asked = b.status === 'discussing' && lastAgent?.author === 'agent' && !active.length
  const needsYou = c.needs + openProposals + (asked && !openProposals ? 1 : 0)
  const parts: string[] = []
  if (needsYou) parts.push(`${needsYou} need${needsYou === 1 ? 's' : ''} you`)
  if (c.running) parts.push(`${c.running} running`)
  if (c.queued) parts.push(`${c.queued} queued`)
  if (c.verifying) parts.push(`${c.verifying} verifying`)
  if (c.landed) parts.push(`${c.landed} landed`)
  if (c.blocked) parts.push(`${c.blocked} blocked`)
  if (!active.length) {
    const head =
      openProposals > 0
        ? `${plural(openProposals, 'proposal')} to review`
        : asked
          ? 'Viko asked a question'
          : 'In conversation'
    return {
      glyph: needsYou ? 'needs-you' : 'active',
      line: [head, ...parts.filter((p) => !p.includes('need'))].join(' · '),
      needsYou,
      terminal: false,
    }
  }
  const allLanded = c.tasks > 0 && c.landed === c.tasks
  const quiet = active.every((a) => ['answered', 'parked', 'noted', 'merged'].includes(a.decision))
  const terminal = !needsYou && !c.blocked && (allLanded || (quiet && !c.tasks)) && !openProposals
  // outcome-polish: follow landed work through to its build (*Done · landed in 0.4.13-t1*).
  const taskIds = active.flatMap((a) => a.targets.filter((t) => t.type === 'task').map((t) => t.id))
  const build = allLanded ? buildFor(taskIds, ctx) : undefined
  const n = c.tasks > 1 ? ` (${c.tasks} tasks)` : ''
  if (allLanded && terminal && build?.status === 'failed')
    return {
      glyph: 'failed',
      line: `Landed${n} · build ${buildLabel(build)} failed`,
      needsYou: 0,
      terminal: false,
      reason: `Build ${buildLabel(build)} failed`,
    }
  if (allLanded && terminal && build && BUILDING.has(build.status))
    return {
      glyph: 'active',
      line: `Landed${n} · building ${buildLabel(build)}`,
      needsYou: 0,
      terminal: false,
    }
  if (allLanded && terminal)
    return {
      glyph: 'done',
      line: `Done · landed${build ? ` in ${buildLabel(build)}` : ''}${n}`,
      needsYou: 0,
      terminal: true,
      settledAt: b.updatedAt,
    }
  return {
    glyph: needsYou ? 'needs-you' : c.blocked ? 'failed' : terminal ? 'done' : 'active',
    line: [verb(active, c), ...parts].join(' · '),
    needsYou,
    terminal,
    ...(c.reason ? { reason: c.reason } : {}),
    ...(terminal ? { settledAt: b.updatedAt } : {}),
  }
}

// --- filters --------------------------------------------------------------------------------------

export type BarkFilter = 'active' | 'needs' | 'done' | 'all'
/** Completed items age out of Active this long after reaching a terminal state. */
export const ACTIVE_AGE_MS = 10 * 60_000

export function matchesFilter(agg: BarkAggregate, f: BarkFilter, now: number): boolean {
  if (f === 'all') return true
  if (f === 'needs') return agg.needsYou > 0 || agg.glyph === 'failed'
  if (f === 'done') return agg.terminal
  return !agg.terminal || now - (agg.settledAt ?? now) < ACTIVE_AGE_MS
}

/** Newest first; anything that needs you or is blocked bubbles to the top of Active. */
export function sortBarks<T extends { bark: BarkRecord; agg: BarkAggregate }>(rows: T[]): T[] {
  const hot = (r: T) => (r.agg.needsYou > 0 || r.agg.glyph === 'failed' ? 1 : 0)
  return [...rows].sort((a, b) => hot(b) - hot(a) || b.bark.createdAt - a.bark.createdAt)
}

// --- live action tree -----------------------------------------------------------------------------

export interface TreeNode {
  key: string
  kind: 'action' | 'task' | 'run' | 'landing' | 'build' | 'answer' | 'steer' | 'decision' | 'other'
  label: string
  /** State chip text. */
  state?: string
  tone?: 'ok' | 'run' | 'wait' | 'fail' | 'idle'
  /** The agent's live line, evidence, commit… */
  detail?: string
  /** Openable resource: `task:<id>`, `run:<id>`. */
  open?: string
  children: TreeNode[]
}

const TONE: Record<TargetState, TreeNode['tone']> = {
  queued: 'idle',
  running: 'run',
  verifying: 'run',
  landed: 'ok',
  'needs-you': 'wait',
  blocked: 'fail',
  failed: 'fail',
  cancelled: 'idle',
  answered: 'ok',
  steered: 'run',
}
const STATE_TEXT: Record<TargetState, string> = {
  queued: 'Queued',
  running: 'Running',
  verifying: 'Checking & landing',
  landed: 'Landed',
  'needs-you': 'Needs you',
  blocked: 'Blocked',
  failed: 'Failed',
  cancelled: 'Cancelled',
  answered: 'Answered',
  steered: 'Steered',
}

const DECISION_LABEL: Record<BarkAction['decision'], string> = {
  answered: 'Answered',
  steered: 'Steered a live run',
  tasks: 'Created tasks',
  merged: 'Merged into existing work',
  parked: 'Parked',
  asked: 'Asked you',
  noted: 'Noted',
  started: 'Started work',
  direct: 'Sent straight to the run',
}

/** bark-route: the routing a started run used (constraint or Viko's pick), as a tree line. */
function routingNode(runId: string, used: BarkRoutingUsed, ctx: AggContext): TreeNode {
  return {
    key: `routing:${runId}`,
    kind: 'other',
    label: `Routing: ${routeLabel(used, (id) => ctx.machines?.get(id)) || 'Auto'}`,
    state: used.source === 'constraint' ? 'Your choice' : 'Viko picked',
    tone: 'idle',
    ...(used.note ? { detail: used.note } : {}),
    children: [],
  }
}

function runNode(
  runId: string,
  run: AggRun | undefined,
  label: string,
  routing?: TreeNode,
): TreeNode {
  const children: TreeNode[] = routing ? [routing] : []
  if (run?.landing)
    children.push({
      key: `landing:${runId}`,
      kind: 'landing',
      label: 'Landing',
      state: run.landing.state,
      tone:
        run.landing.state === 'landed' ? 'ok' : run.landing.state === 'blocked' ? 'fail' : 'run',
      ...(run.landing.sha ? { detail: run.landing.sha.slice(0, 7) } : {}),
      children: run.latestBuild
        ? [
            {
              key: `build:${runId}`,
              kind: 'build',
              label: `Build ${run.latestBuild.number} · ${run.latestBuild.lane}`,
              state: run.latestBuild.state,
              tone: run.latestBuild.state === 'fail' ? 'fail' : 'ok',
              children: [],
            },
          ]
        : [],
    })
  return {
    key: `run:${runId}`,
    kind: 'run',
    label: run?.title ?? label,
    state: run?.status ?? 'unknown',
    tone: run?.status === 'waiting' ? 'wait' : run?.status === 'error' ? 'fail' : 'run',
    ...(run?.activity ? { detail: run.activity } : {}),
    open: `run:${runId}`,
    children,
  }
}

/** One branch per action: task → run → landing → build; steer → run; answer → text. */
export function barkTree(b: BarkRecord, ctx: AggContext): TreeNode[] {
  return b.actions.map((a) => {
    const proposal = a.proposalId ? b.proposals.find((p) => p.id === a.proposalId) : undefined
    const children: TreeNode[] = []
    if (a.answer)
      children.push({
        key: `${a.id}:answer`,
        kind: 'answer',
        label: a.answer.text,
        ...(a.answer.sources.length
          ? { detail: `Sources: ${a.answer.sources.map((s) => s.label || s.ref).join(', ')}` }
          : {}),
        children: [],
      })
    const usedBy = new Map<string, BarkRoutingUsed>()
    for (const t of a.targets) if (t.type === 'run' && t.routing) usedBy.set(t.id, t.routing)
    for (const t of a.targets) {
      if (t.type === 'task') {
        const task = ctx.tasks.get(t.id)
        const run = task?.runId ? ctx.runs.get(task.runId) : undefined
        const s = taskState(task, run)
        children.push({
          key: `${a.id}:task:${t.id}`,
          kind: 'task',
          label: task?.title ?? 'Task',
          state: STATE_TEXT[s],
          tone: TONE[s],
          ...(s === 'blocked' && task?.blockedReason ? { detail: task.blockedReason } : {}),
          open: `task:${t.id}`,
          children: [
            ...(task?.runId
              ? [
                  runNode(
                    task.runId,
                    run,
                    'Run',
                    usedBy.has(task.runId)
                      ? routingNode(task.runId, usedBy.get(task.runId)!, ctx)
                      : undefined,
                  ),
                ]
              : []),
            // outcome-polish: the build that shipped it (openable).
            ...((b2) =>
              b2
                ? [
                    {
                      key: `${a.id}:build:${b2.buildId}`,
                      kind: 'build' as const,
                      label: `In ${buildLabel(b2)}`,
                      state: b2.status,
                      tone: (b2.status === 'failed'
                        ? 'fail'
                        : BUILDING.has(b2.status)
                          ? 'run'
                          : 'ok') as TreeNode['tone'],
                      open: `build:${b2.buildId}`,
                      children: [],
                    },
                  ]
                : [])(s === 'landed' ? buildFor([t.id], ctx) : undefined),
          ],
        })
      } else if (t.type === 'run' && t.role !== 'created') {
        children.push({
          ...runNode(t.id, ctx.runs.get(t.id), 'Live run'),
          key: `${a.id}:run:${t.id}`,
          kind: 'steer',
          label: `Steered: ${ctx.runs.get(t.id)?.title ?? 'live run'}`,
        })
      } else if (t.type === 'decision') {
        children.push({
          key: `${a.id}:decision:${t.id}`,
          kind: 'decision',
          label: 'Decision for you',
          state: 'Needs you',
          tone: 'wait',
          open: `decision:${t.id}`,
          children: [],
        })
      } else if (t.type !== 'run') {
        children.push({
          key: `${a.id}:${t.type}:${t.id}`,
          kind: 'other',
          label: `${t.type} ${t.id.slice(0, 8)}`,
          children: [],
        })
      }
    }
    return {
      key: a.id,
      kind: 'action',
      label: proposal ? proposal.title : DECISION_LABEL[a.decision],
      state:
        a.status === 'active'
          ? DECISION_LABEL[a.decision]
          : a.status === 'undone'
            ? 'Undone'
            : 'Cancelled',
      tone: a.status === 'active' ? 'ok' : 'idle',
      detail: new Date(a.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      children,
    }
  })
}

/** Undo is offered inside the window and before any run started (daemon re-checks). */
export function canUndo(b: BarkRecord, now: number, windowMs = 10 * 60_000): boolean {
  const active = b.actions.filter((a) => a.status === 'active')
  return (
    active.length > 0 &&
    active.every((a) => now - a.at <= windowMs && !a.ops.some((o) => o.op === 'run.started'))
  )
}
